/**
 * 我的收藏组件 - 剧集卡片方式展示
 * 展示用户收藏的剧集列表，支持分页、取消收藏、跳转到播放页
 */

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  CardActions,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Skeleton,
  Rating,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Movie as MovieIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { getFavorites, removeFavorite, getCurrentUserId, formatFavoriteDate } from '../../services/favoriteApi';

// 骨架屏加载组件
const FavoritesSkeleton = () => (
  <Grid container spacing={3} sx={{ p: 2 }}>
    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
        <Card sx={{ borderRadius: 2, height: '100%' }}>
          <Skeleton variant="rounded" width="100%" height={180} />
          <CardContent>
            <Skeleton variant="text" width="80%" height={28} />
            <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="100%" height={40} />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// 空状态组件
const EmptyState = ({ onGoExplore }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      px: 4,
    }}
  >
    <Avatar
      sx={{
        width: 100,
        height: 100,
        bgcolor: 'grey.100',
        color: 'grey.400',
        mb: 3,
      }}
    >
      <MovieIcon sx={{ fontSize: 50 }} />
    </Avatar>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
      暂无收藏剧集
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center', maxWidth: 400 }}>
      发现喜欢的剧集时，点击收藏按钮就可以在这里找到它，方便你随时观看
    </Typography>
    <Button
      variant="contained"
      startIcon={<StarIcon />}
      onClick={onGoExplore}
      sx={{ borderRadius: 2 }}
    >
      去发现好剧
    </Button>
  </Box>
);

// 剧集收藏卡片组件
const FavoriteCard = ({ item, onPlay, onRemoveFavorite, onRemoveSuccess }) => {
  const [removing, setRemoving] = useState(false);

  // 处理取消收藏
  const handleRemoveFavorite = async (e) => {
    e.stopPropagation();
    if (removing) return;

    setRemoving(true);
    try {
      const userId = getCurrentUserId();
      if (!userId) return;

      const result = await removeFavorite({
        user_id: userId,
        drama_id: item.drama_id,
      });

      if (result.success) {
        // 通知父组件刷新列表
        if (onRemoveSuccess) {
          onRemoveSuccess();
        }
      }
    } catch (err) {
      console.error('取消收藏失败:', err);
    } finally {
      setRemoving(false);
    }
  };

  // 格式化集数
  const formatEpisodeCount = (count) => {
    if (!count || count === 0) return '';
    return `共 ${count} 集`;
  };

  // 格式化评分
  const formatRating = (rating) => {
    if (!rating) return null;
    return parseFloat(rating).toFixed(1);
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      {/* 海报区域 */}
      <Box sx={{ position: 'relative' }}>
        <CardActionArea onClick={() => onPlay(item)}>
          <CardMedia
            component="img"
            height="180"
            image={item.drama_cover || '/api/placeholder/300/180'}
            alt={item.drama_title || item.drama_id}
            sx={{
              objectFit: 'cover',
              backgroundColor: 'grey.200',
            }}
          />
          {/* 播放按钮覆盖层 */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.3)',
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 },
            }}
          >
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <PlayArrowIcon sx={{ fontSize: 36 }} />
            </Avatar>
          </Box>
        </CardActionArea>

        {/* 收藏标签 */}
        <Chip
          icon={<FavoriteIcon fontSize="small" />}
          label="已收藏"
          size="small"
          color="error"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            height: 24,
            '& .MuiChip-icon': { ml: 0.5 },
          }}
        />
      </Box>

      {/* 信息区域 */}
      <CardContent sx={{ flexGrow: 1, py: 2, px: 2 }}>
        {/* 剧集名称 */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.drama_title || item.drama_id}
        </Typography>

        {/* 集数和分类 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {item.drama_episode_count > 0 && (
            <Chip
              label={formatEpisodeCount(item.drama_episode_count)}
              size="small"
              variant="outlined"
              sx={{ height: 22 }}
            />
          )}
          {item.drama_category && (
            <Chip
              label={item.drama_category}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22 }}
            />
          )}
        </Box>

        {/* 评分 */}
        {item.drama_rating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Rating value={parseFloat(item.drama_rating) / 2} readOnly size="small" precision={0.5} />
            <Typography variant="body2" color="text.secondary">
              {formatRating(item.drama_rating)}
            </Typography>
          </Box>
        )}

        {/* 简介 */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 1,
          }}
        >
          {item.drama_description || '暂无简介'}
        </Typography>

        {/* 收藏时间 */}
        <Typography variant="caption" color="text.secondary">
          收藏于 {formatFavoriteDate(item.favorited_at)}
        </Typography>
      </CardContent>

      {/* 操作按钮 */}
      <CardActions sx={{ p: 1.5, pt: 0, justifyContent: 'space-between' }}>
        <Tooltip title="立即观看">
          <Button
            size="small"
            startIcon={<PlayArrowIcon />}
            onClick={() => onPlay(item)}
            sx={{ borderRadius: 1.5 }}
          >
            观看
          </Button>
        </Tooltip>
        <Tooltip title="取消收藏">
          <Button
            size="small"
            color="error"
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <FavoriteBorderIcon />}
            onClick={handleRemoveFavorite}
            disabled={removing}
            sx={{ borderRadius: 1.5 }}
          >
            取消收藏
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

const FavoritesTab = ({ user }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favoritesData, setFavoritesData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const PAGE_SIZE = 12;

  // 获取用户ID
  const getUserId = () => {
    if (user && (user.user_id || user._id || user.id)) {
      return user.user_id || user._id || user.id;
    }
    const currentUserId = getCurrentUserId();
    if (currentUserId) {
      return currentUserId;
    }
    return null;
  };

  // 加载收藏列表
  const loadFavorites = async (pageNum = 1) => {
    try {
      setLoading(true);
      setError(null);

      const userId = getCurrentUserId();
      if (!userId) {
        setError('请先登录后查看收藏列表');
        return;
      }

      const result = await getFavorites(userId, pageNum, PAGE_SIZE);

      if (result.success) {
        // 过滤掉已下架的剧集
        // 下架判断：检查 status 字段（0=下架, 1=正常）或 is_offline 字段
        const filteredData = (result.data.favorites || []).filter(item => {
          // 保留状态为正常的剧集
          // status === 1 或 is_offline === false 或 undefined(兼容旧数据)
          if (item.status !== undefined && item.status !== 1) {
            return false;
          }
          if (item.is_offline === true) {
            return false;
          }
          // 兼容字段：offline = true 时过滤
          if (item.offline === true) {
            return false;
          }
          return true;
        });
        
        setFavoritesData(filteredData);
        setTotal(result.data.total || 0); // 总数保持后端返回，仅过滤当前页显示
        setTotalPages(result.data.total_pages || 1);
        setPage(result.data.page || 1);
      } else {
        setError(result.message || '获取收藏列表失败');
      }
    } catch (err) {
      console.error('加载收藏列表失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadFavorites(1);
  }, [user]);

  // 分页切换
  const handlePageChange = (event, value) => {
    setPage(value);
    loadFavorites(value);
  };

  // 刷新
  const handleRefresh = () => {
    loadFavorites(page);
  };

  // 取消收藏成功后的回调
  const handleRemoveSuccess = () => {
    // 刷新当前页
    loadFavorites(page);
  };

  // 跳转到剧集播放页
  const handlePlay = (item) => {
    history.push(`/drama/${item.drama_id}`);
  };

  // 去探索好剧
  const handleGoExplore = () => {
    history.push('/');
  };

  if (loading) {
    return <FavoritesSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              重试
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (favoritesData.length === 0) {
    return <EmptyState onGoExplore={handleGoExplore} />;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* 头部统计信息 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'error.light', color: 'error.dark' }}>
            <FavoriteIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              我的收藏
            </Typography>
            <Typography variant="body2" color="text.secondary">
              共收藏 {total} 部剧集
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ borderRadius: 2 }}
        >
          刷新
        </Button>
      </Box>

      {/* 收藏剧集卡片网格 */}
      <Grid container spacing={3}>
        {favoritesData.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item._id || item.drama_id}>
            <FavoriteCard
              item={item}
              onPlay={handlePlay}
              onRemoveSuccess={handleRemoveSuccess}
            />
          </Grid>
        ))}
      </Grid>

      {/* 分页 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default FavoritesTab;
